/**
 * Theo Voice — Phase 5A.
 *
 * Hybrid architecture:
 *   • Native: Web Speech API (SpeechRecognition for STT, speechSynthesis for TTS)
 *   • Fallback: backend Whisper / OpenAI TTS when native is unavailable
 *
 * Voice gender: ALWAYS female. Native picks Samantha / Karen / Tessa / Microsoft
 * Zira / "Google US English Female", etc. Backend uses OpenAI "nova" (warm
 * female voice).
 *
 * Concurrency: a monotonically-increasing `_gen` counter ensures that:
 *   1. Only one engine speaks at a time (native OR backend, never both).
 *   2. Calling cancelTts() invalidates any in-flight speak — the native→backend
 *      fallback path will NOT fire after a cancel.
 *   3. A new speakText() call automatically preempts the previous one.
 */

const SR_API = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;
const SYNTH = typeof window !== "undefined" ? window.speechSynthesis : null;

export const isNativeSttSupported = () => !!SR_API;
export const isNativeTtsSupported = () => !!(SYNTH && typeof SpeechSynthesisUtterance !== "undefined");

let _nativeSttBlacklisted = false;
export const markNativeSttBroken = () => { _nativeSttBlacklisted = true; };
export const shouldUseNativeStt = () => isNativeSttSupported() && !_nativeSttBlacklisted;


// ──────────────────────────────────────────────────────────────────────────
// Native STT
// ──────────────────────────────────────────────────────────────────────────
export const startNativeRecognition = ({ onInterim, onFinal, onError, lang = "en-US" } = {}) => {
  if (!SR_API) return null;
  const rec = new SR_API();
  rec.lang = lang;
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;

  let finalText = "";
  rec.onresult = (ev) => {
    let interim = "";
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const t = ev.results[i][0].transcript;
      if (ev.results[i].isFinal) finalText += t;
      else interim += t;
    }
    if (interim && onInterim) onInterim(interim);
  };
  rec.onerror = (ev) => {
    console.warn("[Theo:voice] native STT error:", ev.error);
    if (ev.error === "network" || ev.error === "service-not-allowed" || ev.error === "audio-capture") {
      markNativeSttBroken();
    }
    onError?.(ev.error || "unknown");
  };
  rec.onend = () => { if (onFinal) onFinal((finalText || "").trim()); };

  try { rec.start(); }
  catch (e) { onError?.(e.message || "start_failed"); return null; }

  return {
    stop: () => { try { rec.stop(); } catch (_) { /* ignore */ } },
    abort: () => { try { rec.abort(); } catch (_) { /* ignore */ } },
  };
};


// ──────────────────────────────────────────────────────────────────────────
// Backend STT fallback
// ──────────────────────────────────────────────────────────────────────────
export const recordToBackendStt = async ({ onError } = {}) => {
  if (!navigator.mediaDevices?.getUserMedia) {
    onError?.("no_mic_api");
    return { stop: async () => "" };
  }
  let stream;
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch (e) { onError?.("mic_permission_denied"); return { stop: async () => "" }; }

  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  const mime = candidates.find(m => MediaRecorder.isTypeSupported?.(m)) || "";
  const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
  recorder.start();

  const stop = () => new Promise((resolve) => {
    const finish = async () => {
      try { stream.getTracks().forEach(t => t.stop()); } catch (_) { /* ignore */ }
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      if (blob.size === 0) return resolve("");
      const fd = new FormData();
      const ext = (blob.type.includes("webm") ? "webm" : (blob.type.includes("mp4") ? "m4a" : "ogg"));
      fd.append("audio", blob, `theo.${ext}`);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/theo/voice/transcribe`, { method: "POST", body: fd });
        if (!res.ok) { onError?.(`transcribe_${res.status}`); return resolve(""); }
        const data = await res.json();
        resolve((data.text || "").trim());
      } catch (e) {
        onError?.("transcribe_unreachable");
        resolve("");
      }
    };
    if (recorder.state === "inactive") finish();
    else { recorder.onstop = finish; try { recorder.stop(); } catch (_) { finish(); } }
  });
  return { stop };
};


// ──────────────────────────────────────────────────────────────────────────
// TTS — single-engine guarantee via generation counter
// ──────────────────────────────────────────────────────────────────────────
let _gen = 0;                  // bumps on every cancel + every speakText
let _activeUtterance = null;   // current native utterance
let _activeAudio = null;       // current backend HTMLAudio element
let _cachedVoice = null;       // memoized native voice pick

/** Native voice priority: female English voices in known order. */
const FEMALE_VOICE_PATTERNS = [
  // iOS / macOS Safari
  /^Samantha/i,
  /^Karen/i,
  /^Moira/i,
  /^Tessa/i,
  /^Fiona/i,
  /^Victoria/i,
  /^Allison/i,
  /^Susan/i,
  /^Ava/i,
  // Windows
  /Microsoft Zira/i,
  /Microsoft Aria/i,
  /Microsoft Jenny/i,
  // Chrome (Linux/Android)
  /Google US English/i,            // typically female on Chrome
  /Google UK English Female/i,
];

const pickVoice = () => {
  if (!SYNTH) return null;
  if (_cachedVoice) return _cachedVoice;
  const voices = SYNTH.getVoices() || [];
  if (!voices.length) return null;  // voices may load async; we'll re-pick later

  // 1. Try our known-female patterns in priority order
  for (const re of FEMALE_VOICE_PATTERNS) {
    const v = voices.find(x => re.test(x.name) && /en/i.test(x.lang));
    if (v) { _cachedVoice = v; return v; }
  }
  // 2. Any voice whose name contains "Female"
  const explicit = voices.find(v => /female/i.test(v.name) && /en/i.test(v.lang));
  if (explicit) { _cachedVoice = explicit; return explicit; }
  // 3. en-US fallback
  const enUs = voices.find(v => /en[-_]US/i.test(v.lang));
  if (enUs) { _cachedVoice = enUs; return enUs; }
  // 4. anything
  _cachedVoice = voices[0];
  return voices[0];
};

// Warm up voice list on iOS Safari (voices arrive after voiceschanged event)
if (SYNTH) {
  try {
    SYNTH.onvoiceschanged = () => { _cachedVoice = null; pickVoice(); };
    // Trigger initial fetch
    SYNTH.getVoices();
  } catch (_) { /* ignore */ }
}

/** Stop EVERYTHING — native + backend. Increments `_gen` so any in-flight
 *  speakText() promise sees its generation as stale and exits without
 *  triggering the backend fallback. */
export const cancelTts = () => {
  _gen++;
  try { SYNTH?.cancel(); } catch (_) { /* ignore */ }
  if (_activeAudio) {
    try {
      _activeAudio.pause();
      _activeAudio.removeAttribute("src");
      _activeAudio.load();
    } catch (_) { /* ignore */ }
    _activeAudio = null;
  }
  _activeUtterance = null;
};

const speakNative = (text, gen) => new Promise((resolve, reject) => {
  if (!SYNTH) return resolve({ ok: false, reason: "no_synth" });
  // Ensure no overlap on platforms that queue utterances
  try { SYNTH.cancel(); } catch (_) { /* ignore */ }
  // Wait one tick for cancel to settle (Safari quirk)
  setTimeout(() => {
    if (gen !== _gen) return resolve({ ok: false, reason: "preempted" });
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice();
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 1.0;
    u.lang = (v && v.lang) || "en-US";
    let settled = false;
    const cleanup = () => { if (_activeUtterance === u) _activeUtterance = null; };
    u.onend = () => {
      if (settled) return; settled = true;
      cleanup();
      // If this utterance was cancelled by a newer gen, mark preempted
      if (gen !== _gen) resolve({ ok: false, reason: "preempted" });
      else resolve({ ok: true });
    };
    u.onerror = (e) => {
      if (settled) return; settled = true;
      cleanup();
      const err = e?.error || "speak_failed";
      // Browser-fired cancel maps to onerror with "interrupted" / "canceled"
      // — treat as preempted (don't fall through to backend).
      if (gen !== _gen || err === "interrupted" || err === "canceled") {
        resolve({ ok: false, reason: "preempted" });
      } else {
        reject(err);
      }
    };
    _activeUtterance = u;
    try { SYNTH.speak(u); }
    catch (e) {
      settled = true; cleanup();
      reject(e?.message || "speak_throw");
    }
  }, 60);
});

const speakBackend = (text, gen) => new Promise(async (resolve) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${backendUrl}/api/theo/voice/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, voice: "nova" }),  // female voice
    });
    if (gen !== _gen) return resolve(false);  // preempted while awaiting fetch
    if (!res.ok) return resolve(false);
    const blob = await res.blob();
    if (gen !== _gen) return resolve(false);
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    _activeAudio = audio;
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (_activeAudio === audio) _activeAudio = null;
      resolve(true);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      if (_activeAudio === audio) _activeAudio = null;
      resolve(false);
    };
    try { await audio.play(); }
    catch (_) {
      URL.revokeObjectURL(url);
      if (_activeAudio === audio) _activeAudio = null;
      resolve(false);
    }
  } catch (_) {
    resolve(false);
  }
});

// Matches every Unicode-defined emoji, pictograph, symbol, dingbat,
// regional-indicator, ZWJ joiner, and variation selector. Strips them
// before TTS so the synth doesn't try to read "✨" as "sparkles".
const EMOJI_REGEX = /[\u{1F1E0}-\u{1F1FF}\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B00}-\u{2BFF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F1FF}\u{1F200}-\u{1F2FF}\u{200D}\u{20E3}\u{FE0F}\u{20D0}-\u{20FF}]/gu;

/** Strip emojis + collapse the whitespace they left behind. Visible chat
 *  text is unchanged — this only applies to what we send to the synth. */
const stripForSpeech = (text) => (text || "")
  .replace(/<theo_state_update>[\s\S]*?<\/theo_state_update>/g, "")
  .replace(EMOJI_REGEX, "")
  // Collapse double-spaces / orphaned punctuation runs the emoji left behind
  .replace(/[ \t]{2,}/g, " ")
  .replace(/\s+([,.!?;:])/g, "$1")
  .trim();

/**
 * Speak text aloud. ALWAYS preempts any in-progress speech. Native first,
 * backend only as a fallback for unsupported browsers / native failures.
 * Returns true on successful playback, false otherwise.
 */
export const speakText = async (text) => {
  const cleaned = stripForSpeech(text);
  if (!cleaned) return false;

  // Preempt anything currently speaking. This bumps _gen, so any older
  // in-flight speak() will see myGen !== _gen and exit cleanly.
  cancelTts();
  const myGen = _gen;

  if (isNativeTtsSupported()) {
    try {
      const result = await speakNative(cleaned, myGen);
      if (myGen !== _gen) return false;          // a newer call already took over
      if (result.ok) return true;
      // If preempted, do NOT fall back — the new caller has its own speech.
      if (result.reason === "preempted") return false;
      // Otherwise: native said no_synth / unknown — try backend.
    } catch (_) {
      if (myGen !== _gen) return false;          // preempted while native was running
      // Real native failure → fall through to backend
    }
  }

  if (myGen !== _gen) return false;
  return speakBackend(cleaned, myGen);
};

/** Whether either engine is currently producing speech. */
export const isSpeaking = () =>
  !!(_activeUtterance || _activeAudio || (SYNTH && SYNTH.speaking));
